import React, { useEffect, useState } from 'react';
import { BuildingIcon, CalendarDaysIcon, CalendarIcon, ClipboardListIcon, DatabaseIcon, FileSpreadsheetIcon, GraduationCapIcon, MailIcon, MegaphoneIcon, MessageCircleIcon, SendIcon, TargetIcon, WebhookIcon, WorkflowIcon, ZapIcon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { GOOGLE_CALENDAR, META_PIXEL, connectGoogleCalendar, connectMetaPixel, getIntegration, listIntegrations } from '../../services/integrationService';
import { META_PIXEL_EVENT_INFO } from '../../data/metaPixel';
import { isValidPixelId } from '../../utils/metaPixel';
import { MetaPixelEvents } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Switch } from '../../components/ui/Switch';
import { Tag } from '../../components/ui/Tag';
const ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  Database: DatabaseIcon,
  Megaphone: MegaphoneIcon,
  MessageCircle: MessageCircleIcon,
  CalendarDays: CalendarDaysIcon,
  GraduationCap: GraduationCapIcon,
  ClipboardList: ClipboardListIcon,
  FileSpreadsheet: FileSpreadsheetIcon,
  Webhook: WebhookIcon,
  Mail: MailIcon,
  Calendar: CalendarIcon,
  Target: TargetIcon,
  Zap: ZapIcon,
  Workflow: WorkflowIcon,
  Send: SendIcon
};

/** Integraciones con configuración real en el frontend. */
const GOOGLE_CALENDAR_ID = 'google-calendar';
const META_PIXEL_ID = 'meta-pixel';
export function Integrations() {
  const {
    organizationId
  } = useWorkspace();
  const integrations = listIntegrations();
  const [selected, setSelected] = useState<string | null>(null);
  const [bookingUrl, setBookingUrl] = useState('');
  const [connectedUrl, setConnectedUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Meta Pixel: un único Pixel ID por organización; las acciones se
  // distinguen por evento (PageView, Lead, WhatsApp, Schedule), no por Pixel.
  const [pixelId, setPixelId] = useState('');
  const [pixelEnabled, setPixelEnabled] = useState(false);
  const [pixelEvents, setPixelEvents] = useState<Required<MetaPixelEvents>>({
    pageView: true,
    lead: true,
    whatsapp: true,
    schedule: false
  });
  const [connectedPixelId, setConnectedPixelId] = useState('');
  useEffect(() => {
    let active = true;
    getIntegration(organizationId, GOOGLE_CALENDAR).then(data => {
      if (!active) return;
      const url = String(data?.config?.bookingUrl ?? '');
      setConnectedUrl(url);
      setBookingUrl(url);
    });
    getIntegration(organizationId, META_PIXEL).then(data => {
      if (!active) return;
      const config = (data?.config ?? {}) as {
        pixelId?: string;
        events?: MetaPixelEvents;
      };
      const id = String(config.pixelId ?? '');
      setPixelId(id);
      setConnectedPixelId(data?.isActive ? id : '');
      setPixelEnabled(Boolean(data?.isActive));
      setPixelEvents({
        pageView: config.events?.pageView !== false,
        lead: config.events?.lead !== false,
        whatsapp: config.events?.whatsapp !== false,
        schedule: Boolean(config.events?.schedule)
      });
    });
    return () => {
      active = false;
    };
  }, [organizationId]);
  const current = integrations.find(i => i.id === selected);
  const isCalendar = current?.id === GOOGLE_CALENDAR_ID;
  const isMetaPixel = current?.id === META_PIXEL_ID;
  async function handleConnect() {
    setSaving(true);
    setError(null);
    try {
      await connectGoogleCalendar(organizationId, bookingUrl.trim());
      setConnectedUrl(bookingUrl.trim());
      setSelected(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar.');
    } finally {
      setSaving(false);
    }
  }
  async function handleConnectMetaPixel() {
    setSaving(true);
    setError(null);
    try {
      await connectMetaPixel(organizationId, {
        pixelId: pixelId.trim(),
        enabled: pixelEnabled,
        events: pixelEvents
      });
      setConnectedPixelId(pixelEnabled ? pixelId.trim() : '');
      setSelected(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar.');
    } finally {
      setSaving(false);
    }
  }
  return <>
      <PageHeader title="Integraciones" description="Conecta AVA con las herramientas que ya usa tu empresa." data-id="element-1345" data-mp="2smr3oqdc7vyh" />

      <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-id="element-1346" data-mp="2xf23zubzpjrk">
        {integrations.map(integration => {
        const Icon = ICONS[integration.icon] ?? BuildingIcon;
        const soon = integration.status === 'proximamente';
        const calendar = integration.id === GOOGLE_CALENDAR_ID;
        const metaPixel = integration.id === META_PIXEL_ID;
        const connected = calendar ? Boolean(connectedUrl) : metaPixel ? Boolean(connectedPixelId) : false;
        return <li key={integration.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1347" data-mp="uq0yfwgjd83e">
              <div className="flex items-start gap-3" data-id="element-1348" data-mp="lqnl2spajzde">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-primary/10" data-id="element-1349" data-mp="ls8fmiblvib9">
                  <Icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-1350" data-mp="1iir2lji76n34" />
                </span>
                <div className="min-w-0" data-id="element-1351" data-mp="1eh5u0acp4uyq">
                  <h2 className="text-sm font-semibold text-foreground" data-id="element-1352" data-mp="19njiili9qx4i">
                    {integration.name}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground" data-id="element-1353" data-mp="14oqc1d1eg6du">
                    {calendar ? 'Calendario global de reservas para todas tus landings.' : metaPixel ? 'Pixel global de la organización para medir conversiones.' : integration.description}
                  </p>
                  {calendar && connected && <p className="mt-2 truncate text-xs text-muted-foreground" data-id="element-1354" data-mp="2lbd89e1l8jdm">
                      {connectedUrl}
                    </p>}
                  {metaPixel && connected && <p className="mt-2 truncate text-xs text-muted-foreground" data-id="element-1355" data-mp="2bh34l0773ynl">
                      ID {connectedPixelId}
                    </p>}
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between gap-3 border-t border-border pt-4" data-id="element-1356" data-mp="1oizvak4vawat">
                <Tag data-id="element-1357" data-mp="1skbk4nfx3dhy">
                  {soon ? 'Próximamente' : connected ? 'Conectado' : 'No conectado'}
                </Tag>
                <Button size="sm" variant={soon ? 'ghost' : 'secondary'} disabled={soon} onClick={() => setSelected(integration.id)} data-id="element-1358" data-mp="3iy2g44iz1q7e">
                  {connected ? 'Configurar' : 'Conectar'}
                </Button>
              </div>
            </li>;
      })}
      </ul>

      <Modal open={Boolean(current)} onClose={() => setSelected(null)} title={current ? `Conectar ${current.name}` : ''} footer={isCalendar ? <div className="flex justify-end gap-2" data-id="element-1360" data-mp="1uyzyzhp9tbli">
              <Button variant="ghost" onClick={() => setSelected(null)} data-id="element-1361" data-mp="1uxowrmbh4bwi">
                Cancelar
              </Button>
              <Button onClick={handleConnect} loading={saving} data-id="element-1362" data-mp="1q29jowlaik15">
                Guardar y aplicar
              </Button>
            </div> : isMetaPixel ? <div className="flex justify-end gap-2" data-id="element-1363" data-mp="1z24zxftpd581">
              <Button variant="ghost" onClick={() => setSelected(null)} data-id="element-1364" data-mp="9lnghy6l711h">
                Cancelar
              </Button>
              <Button onClick={handleConnectMetaPixel} loading={saving} disabled={pixelEnabled && !isValidPixelId(pixelId)} data-id="element-1365" data-mp="4nly7fgzj5r2">
                Guardar configuración
              </Button>
            </div> : <div className="flex justify-end" data-id="element-1366" data-mp="3op45rjnof76l">
              <Button variant="secondary" onClick={() => setSelected(null)} data-id="element-1367" data-mp="3opdtt1p5v49n">
                Entendido
              </Button>
            </div>} data-id="element-1359" data-mp="1l0sn3d9u9jqd">
        {isCalendar ? <div className="space-y-4" data-id="element-1368" data-mp="2wz92ej2zhgyx">
            <p className="text-sm text-muted-foreground" data-id="element-1369" data-mp="30z9rcxnplak8">
              Pega el enlace público de reservas de tu Google Calendar. Se usará
              automáticamente en todas las landings que utilicen el calendario
              principal, sin configurarlas una a una.
            </p>
            <Input id="google-calendar-url" label="Enlace de reservas" type="url" placeholder="https://calendar.app.google/tu-enlace" value={bookingUrl} onChange={e => setBookingUrl(e.target.value)} data-id="element-1370" data-mp="3av30s864snxs" />
            {error && <p role="alert" className="text-sm text-red-600" data-id="element-1371" data-mp="25qg06ouscknh">
                {error}
              </p>}
          </div> : isMetaPixel ? <div className="space-y-4" data-id="element-1372" data-mp="20xmqdmkgmm4j">
            <p className="text-sm text-muted-foreground" data-id="element-1373" data-mp="1w0mpdq2gw9ka">
              Un único Pixel ID por organización. No se carga en ninguna
              landing hasta que lo actives y guardes aquí.
            </p>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3" data-id="element-1374" data-mp="1mz58w6l5e5v2">
              <span className="text-sm font-medium text-foreground" data-id="element-1375" data-mp="1hzsd2wcatogc">Meta Pixel</span>
              <Switch id="pixel-enabled" label="Activar" checked={pixelEnabled} onChange={setPixelEnabled} data-id="element-1376" data-mp="1d20ks17qs0bm" />
            </div>
            <Input id="pixel-id" label="Pixel ID" placeholder="123456789012345" value={pixelId} onChange={e => setPixelId(e.target.value.trim())} data-id="element-1377" data-mp="3cdjuii0vdcmc" />
            {pixelId && !isValidPixelId(pixelId) && <p role="alert" className="text-sm text-red-600" data-id="element-1378" data-mp="33ndl8fmvcdvt">
                Introduce un ID de Meta Pixel válido.
              </p>}
            <div data-id="element-1379" data-mp="3ihs6kmtwnzdn">
              <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1380" data-mp="3dks04at58um0">Eventos activos</p>
              <div className="space-y-2" data-id="element-1381" data-mp="3dixib5d65im5">
                {META_PIXEL_EVENT_INFO.map(event => <div key={event.key} className={event.disabled ? 'opacity-60' : ''} data-id="element-1382" data-mp="2psutqz8gqusa">
                    <Checkbox label={event.label} checked={Boolean(pixelEvents[event.key])} disabled={event.disabled} onChange={e => setPixelEvents(prev => ({
                ...prev,
                [event.key]: e.target.checked
              }))} data-id="element-1383" data-mp="2ku00f3cvdntx" />
                    <p className="ml-6 text-xs text-muted-foreground" data-id="element-1384" data-mp="224tmruqxnmez">{event.description}</p>
                  </div>)}
              </div>
            </div>
            {error && <p role="alert" className="text-sm text-red-600" data-id="element-1385" data-mp="mfc6ekhciumq">
                {error}
              </p>}
          </div> : <p className="text-sm text-muted-foreground" data-id="element-1386" data-mp="7njwwp48gasx">
            La conexión con {current?.name} se activará cuando se configuren las
            credenciales correspondientes. Ninguna clave se guarda en el navegador.
          </p>}
      </Modal>
    </>;
}